import type { ApDashboard } from '@dcc-erp/shared';
import { fmt, fmtB, toNumber } from '@dcc-erp/shared';

import { useApDashboard } from '../../lib/queries/ap';

// =============================================================================
// AP KPI dashboard tab (AP Phase C7a) — a READ-ONLY company-wide view from
// useApDashboard: a KPI-card row (DPO / on-time% / discount-capture% / touchless%
// / outstanding / overdue), the aging buckets as a small CSS-bar breakdown, the
// top-5 vendors by outstanding, and a 6-month billed-vs-paid mini bar trend. No
// new chart dependency — plain CSS bars (inline width/height %).
// =============================================================================
export interface ApDashboardTabProps {
  companyId: string | undefined;
}

// One headline KPI card. `value` is already formatted; `sub` is the faint line.
function DashKpiCard({
  label,
  value,
  unit,
  sub,
  dotColor,
}: {
  label: string;
  value: string;
  unit?: string | undefined;
  sub?: string | undefined;
  dotColor?: string | undefined;
}) {
  return (
    <div className="kpi">
      <div className="label">
        <span className="dot" style={dotColor ? { background: dotColor } : undefined} />
        {label}
      </div>
      <div className="value tnum">
        {value}
        {unit ? <span className="unit"> {unit}</span> : null}
      </div>
      {sub ? <div className="trend faint">{sub}</div> : null}
    </div>
  );
}

// Render a percentage KPI (1 dp) or an em-dash when null.
function pctOrDash(v: number | null | undefined): string {
  return v == null ? '—' : `${v.toFixed(1)}%`;
}

const AGING_BUCKETS: readonly [keyof ApDashboard['aging'], string][] = [
  ['notDue', 'ยังไม่ครบกำหนด'],
  ['d1_30', '1-30 วัน'],
  ['d31_60', '31-60 วัน'],
  ['d61_90', '61-90 วัน'],
  ['over90', '90+ วัน'],
];

const AGING_COLORS: Record<keyof ApDashboard['aging'], string> = {
  notDue: 'var(--brand, #2563eb)',
  d1_30: 'var(--info, #0ea5e9)',
  d31_60: 'var(--warning, #f59e0b)',
  d61_90: '#f97316',
  over90: 'var(--negative, #ef4444)',
};

export function ApDashboardTab({ companyId }: ApDashboardTabProps) {
  const { data: dash, isPending, error } = useApDashboard(companyId);

  if (error) {
    return (
      <div className="tbl-shell">
        <div className="neg" style={{ textAlign: 'center', padding: 24 }}>
          โหลดไม่สำเร็จ: {error.message}
        </div>
      </div>
    );
  }

  if (isPending || !dash) {
    return (
      <div className="tbl-shell">
        <div className="muted" style={{ textAlign: 'center', padding: 24 }}>
          กำลังโหลด…
        </div>
      </div>
    );
  }

  const agingMax = Math.max(
    ...AGING_BUCKETS.map(([k]) => toNumber(dash.aging[k])),
    1, // avoid /0 for an all-zero aging
  );
  const trendMax = Math.max(
    ...dash.monthlyTrend.flatMap((t) => [toNumber(t.billed), toNumber(t.paid)]),
    1,
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Headline + process KPI cards. */}
      <div className="kpi-grid" data-testid="ap-dashboard-kpis">
        <DashKpiCard
          label="DPO เฉลี่ย"
          value={dash.dpo == null ? '—' : String(dash.dpo)}
          unit={dash.dpo == null ? undefined : 'วัน'}
          sub="ระยะเครดิตเฉลี่ย (บิลที่จ่ายแล้ว)"
        />
        <DashKpiCard
          label="จ่ายตรงเวลา"
          value={pctOrDash(dash.onTimePaymentPct)}
          dotColor="var(--positive)"
          sub="จ่าย ≤ กำหนดของบิลที่ตัดจ่าย"
        />
        <DashKpiCard
          label="รับส่วนลดจ่ายเร็ว"
          value={pctOrDash(dash.discountCapturePct)}
          dotColor="var(--info, #0ea5e9)"
          sub="ส่วนลดที่รับ ÷ ที่ทำได้"
        />
        <DashKpiCard
          label="อัตโนมัติ (touchless)"
          value={pctOrDash(dash.touchlessPct)}
          dotColor="var(--brand)"
          sub="ประมาณการ · ที่ไม่เข้าสายอนุมัติ"
        />
        <DashKpiCard
          label="คงค้างจ่าย"
          value={fmtB(toNumber(dash.outstandingTotal))}
          unit="บาท"
          dotColor="var(--warning)"
        />
        <DashKpiCard
          label="เกินกำหนด"
          value={fmtB(toNumber(dash.overdueTotal))}
          unit="บาท"
          dotColor="var(--negative)"
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 16,
        }}
      >
        {/* Aging breakdown — CSS bars per bucket. */}
        <div className="tbl-shell" data-testid="ap-dashboard-aging">
          <div className="section-title" style={{ margin: '4px 4px 12px' }}>
            อายุหนี้คงค้าง (ณ {dash.asOf})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 6px 10px' }}>
            {AGING_BUCKETS.map(([k, label]) => {
              const v = toNumber(dash.aging[k]);
              const widthPct = (v / agingMax) * 100;
              return (
                <div key={k} data-testid={`aging-bar-${k}`}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: 13,
                      marginBottom: 4,
                    }}
                  >
                    <span className="muted">{label}</span>
                    <span className="tnum">{fmt(v, 2)}</span>
                  </div>
                  <div
                    style={{
                      background: 'var(--surface-2, #f1f5f9)',
                      borderRadius: 4,
                      height: 10,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${widthPct}%`,
                        height: '100%',
                        background: AGING_COLORS[k],
                        borderRadius: 4,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top-5 vendors by outstanding. */}
        <div className="tbl-shell" data-testid="ap-dashboard-top-vendors">
          <div className="section-title" style={{ margin: '4px 4px 12px' }}>
            เจ้าหนี้คงค้างสูงสุด
          </div>
          {dash.topVendors.length === 0 ? (
            <div className="muted" style={{ padding: '0 6px 12px', fontSize: 13 }}>
              ไม่มียอดค้างจ่าย
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>ผู้ขาย</th>
                  <th className="num">คงค้าง</th>
                </tr>
              </thead>
              <tbody>
                {dash.topVendors.map((v) => (
                  <tr key={v.vendorId}>
                    <td>{v.name}</td>
                    <td className="num tnum">{fmt(toNumber(v.outstanding), 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* 6-month billed-vs-paid mini bar trend (CSS bars). */}
      <div className="tbl-shell" data-testid="ap-dashboard-trend">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            margin: '4px 4px 12px',
          }}
        >
          <div className="section-title" style={{ margin: 0 }}>
            แนวโน้ม 6 เดือน (ตั้งบิล vs จ่าย)
          </div>
          <div style={{ display: 'flex', gap: 14, fontSize: 12 }} className="muted">
            <span>
              <span
                style={{
                  display: 'inline-block',
                  width: 9,
                  height: 9,
                  background: 'var(--brand, #2563eb)',
                  borderRadius: 2,
                  marginRight: 4,
                }}
              />
              ตั้งบิล
            </span>
            <span>
              <span
                style={{
                  display: 'inline-block',
                  width: 9,
                  height: 9,
                  background: 'var(--positive, #16a34a)',
                  borderRadius: 2,
                  marginRight: 4,
                }}
              />
              จ่าย
            </span>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 12,
            height: 160,
            padding: '0 8px 4px',
          }}
        >
          {dash.monthlyTrend.map((t) => {
            const billed = toNumber(t.billed);
            const paid = toNumber(t.paid);
            return (
              <div
                key={t.period}
                data-testid={`trend-col-${t.period}`}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4,
                  height: '100%',
                }}
              >
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 3,
                    width: '100%',
                    justifyContent: 'center',
                  }}
                >
                  <div
                    title={`ตั้งบิล ${fmt(billed, 2)}`}
                    style={{
                      width: 14,
                      height: `${(billed / trendMax) * 100}%`,
                      background: 'var(--brand, #2563eb)',
                      borderRadius: '2px 2px 0 0',
                    }}
                  />
                  <div
                    title={`จ่าย ${fmt(paid, 2)}`}
                    style={{
                      width: 14,
                      height: `${(paid / trendMax) * 100}%`,
                      background: 'var(--positive, #16a34a)',
                      borderRadius: '2px 2px 0 0',
                    }}
                  />
                </div>
                <div className="muted" style={{ fontSize: 11 }}>
                  {t.period.slice(4)}/{t.period.slice(2, 4)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
